import { useRef, useState } from "react";
import { useHospitalData } from "../../../../contexts/HospitalDataProvider";
import type { DataLoadPhase } from "./consts";
import { allFilesSettled, nextPendingFile } from "./dataLoadOrder";
import { emptySeeds, type PromptSeeds } from "./dataLoadPrompts";
import { useDataLoadFiles } from "./useDataLoadFiles";
import { useDataLoadPrompts } from "./useDataLoadPrompts";
import { useDataLoadTransform } from "./useDataLoadTransform";
import { useDataLoadUpload } from "./useDataLoadUpload";

/**
 * Composition root for the Data Load tab. Owns the two department-level controls and the phase, and
 * wires the file list, the prompt seeding and the run pipeline together.
 *
 * The seeds ref lives here rather than inside `useDataLoadPrompts` because both of the other hooks
 * need it: the prompts hook writes it when history arrives, and the files hook reads it on every add
 * and every kind change.
 */
export function useDataLoadSection() {
	const { chosenDepartment } = useHospitalData();
	const departmentId = chosenDepartment?.department_id;

	const [phase, setPhase] = useState<DataLoadPhase>("configure");
	const [allowDuplicates, setAllowDuplicates] = useState(false);
	const [minStartDate, setMinStartDate] = useState("");

	const seedsRef = useRef<PromptSeeds>(emptySeeds());
	const runningRef = useRef(false);

	const filesApi = useDataLoadFiles({ seedsRef });
	// The load chain captures its queue before the first await; this is how it re-reads a row it is
	// about to load, so a mid-run Remove or Skip is honoured instead of silently ignored.
	const filesRef = useRef(filesApi.files);
	filesRef.current = filesApi.files;
	useDataLoadPrompts({ departmentId, seedsRef, setFiles: filesApi.setFiles, frozenRef: runningRef });

	const transformApi = useDataLoadTransform({
		departmentId,
		files: filesApi.files,
		updateFile: filesApi.updateFile,
		onDone: () => setPhase("results"),
	});

	const uploadApi = useDataLoadUpload({
		departmentId,
		orderedFiles: filesApi.orderedFiles,
		filesRef,
		updateFile: filesApi.updateFile,
		allowDuplicates,
		minStartDate,
	});

	const isRunning = transformApi.isRecording || transformApi.isTransforming || uploadApi.isLoading;

	// Read by the prompt seeding and by the destructive per-file controls, both of which must stop
	// while the sequential load chain is holding a snapshot of the list.
	runningRef.current = isRunning;

	const resetAll = () => {
		filesApi.resetFiles();
		uploadApi.resetRun();
		setAllowDuplicates(false);
		setMinStartDate("");
		setPhase("configure");
	};

	const canRun = Boolean(departmentId) && filesApi.files.length > 0 && filesApi.unsetKindCount === 0 && !isRunning;
	const nextToUpload = nextPendingFile(filesApi.orderedFiles);

	return {
		...filesApi,
		...transformApi,
		...uploadApi,
		isRunning,
		phase,
		departmentId,
		chosenDepartment,
		allowDuplicates,
		setAllowDuplicates,
		minStartDate,
		setMinStartDate,
		canRun,
		nextToUpload,
		allSettled: allFilesSettled(filesApi.files),
		locked: isRunning,
		resetAll,
	};
}
